/**
 * Initialize and manage a star canvas animation
 * @param {string} canvasId - The ID of the canvas element
 * @param {Object} options - Configuration options
 * @param {number} options.starCount - Number of stars (default: 400)
 * @param {string} options.backgroundColor - Background color (default: '#111')
 * @param {number} options.minSpeed - Minimum star speed (default: 0.2)
 * @param {number} options.maxSpeed - Maximum star speed (default: 0.7)
 * @param {number} options.minSize - Minimum star size (default: 1)
 * @param {number} options.maxSize - Maximum star size (default: 2)
 * @param {number} options.gravityStrength - Mouse gravity strength (default: 5)
 * @param {number} options.maxGravityForce - Maximum gravity force (default: 0.05)
 * @param {number} options.damping - Velocity damping factor (default: 0.95)
 * @param {number} options.gravityRadius - Radius of mouse gravity effect (default: 150)
 * @returns {Function} Cleanup function to remove event listeners and stop animation
 */
// Mobile detection utility
export const isMobileDevice = () => {
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) ||
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0
  );
};

export const initializeStarCanvas = (
  canvasId = 'star-canvas',
  options = {}
) => {
  const {
    starCount = 400,
    backgroundColor = '#111',
    minSpeed = 0.2,
    maxSpeed = 0.7,
    minSize = 1,
    maxSize = 2,
    gravityStrength = 2,
    maxGravityForce = 0.05,
    damping = 0.95,
    gravityRadius = 250,
  } = options;

  const canvas = document.getElementById(canvasId);
  if (!canvas) {
    console.warn(`Canvas with id '${canvasId}' not found`);
    return () => {};
  }

  const ctx = canvas.getContext('2d');
  let width = (canvas.width = window.innerWidth);
  let height = (canvas.height = window.innerHeight);
  let animationId;

  // Mouse tracking
  let mouseX = width / 2;
  let mouseY = height / 2;

  // Device detection - disable gravity on mobile/touch devices
  const isMobile = isMobileDevice();

  const stars = Array.from({ length: starCount }, () => {
    const baseSpeed = Math.random() * (maxSpeed - minSpeed) + minSpeed;
    return {
      x: Math.random() * width,
      y: Math.random() * height,
      size: Math.floor(Math.random() * (maxSize - minSize + 1)) + minSize,
      baseSpeed,
      vx: baseSpeed, // velocity x
      vy: 0, // velocity y
    };
  });

  function draw() {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);

    stars.forEach((star) => {
      // Calculate distance to mouse
      const dx = mouseX - star.x;
      const dy = mouseY - star.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Apply gravitational force only on desktop devices
      if (!isMobile && distance > 0 && distance < gravityRadius) {
        const force = Math.min(
          gravityStrength / (distance + 1),
          maxGravityForce
        );
        const angle = Math.atan2(dy, dx);

        // Update velocity with gravity
        star.vx += Math.cos(angle) * force;
        star.vy += Math.sin(angle) * force;
      }

      // Apply damping and maintain base horizontal movement
      star.vx = star.vx * damping + star.baseSpeed * (1 - damping);
      star.vy *= damping;

      // Update position
      star.x += star.vx;
      star.y += star.vy;

      // Wrap around screen horizontally
      if (star.x > width) {
        star.x = 0;
        star.y = Math.random() * height;
        star.vx = star.baseSpeed;
        star.vy = 0;
      }

      // Keep stars within vertical bounds
      if (star.y < 0) star.y = 0;
      if (star.y > height) star.y = height;

      // Draw star
      const grey = Math.floor(Math.random() * 100) + 155;
      ctx.fillStyle = `rgb(${grey}, ${grey}, ${grey})`;
      ctx.fillRect(
        Math.floor(star.x),
        Math.floor(star.y),
        star.size,
        star.size
      );
    });

    animationId = requestAnimationFrame(draw);
  }

  const handleResize = () => {
    width = canvas.width = window.innerWidth;
    height = canvas.height = window.innerHeight;
    mouseX = width / 2;
    mouseY = height / 2;
  };

  const handleMouseMove = (event) => {
    mouseX = event.clientX;
    mouseY = event.clientY;
  };

  // Start the animation
  draw();
  window.addEventListener('resize', handleResize);
  window.addEventListener('mousemove', handleMouseMove);

  // Return cleanup function
  return () => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('mousemove', handleMouseMove);
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
  };
};

/**
 * Format date for display using user's locale and timezone
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string or 'Never' if no date provided
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'Never';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return 'Invalid date';
    }

    // Get user's locale and timezone
    const userLocale = navigator.language || 'en-US';
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Format with explicit locale and timezone
    return new Intl.DateTimeFormat(userLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimeZone,
      timeZoneName: 'short',
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error, 'Input:', dateString);
    return 'Error formatting date';
  }
};

export const formatTimeAgo = (date) => {
  const now = new Date();
  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24)
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7)
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4)
    return `${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'} ago`;

  const diffInMonths = Math.floor(diffInDays / 30);
  return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
};

export const formatDateTimeStamp = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const formatDateWithMonthYear = (dateString) => {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Global role constants for consistent role checks in the client
export const ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  MODERATOR: 'moderator',
};

/**
 * Sort an array of objects by endYear field descending (most recent first)
 * @param {Array} list - Array of objects with endYear field
 * @returns {Array} Sorted array (new array, does not mutate original)
 */
export const sortByEndYear = (list) => {
  if (!list || !Array.isArray(list)) return [];
  return [...list].sort((a, b) => {
    const yearA = parseInt(a.endYear) || 0;
    const yearB = parseInt(b.endYear) || 0;
    return yearB - yearA;
  });
};

// Add your global helper functions below this line
// Example:
// export const yourHelperFunction = () => {
//   // Your implementation
// };
